import React, { useState, useEffect, useRef } from 'react';
import {
    Calendar,
    Clock,
    ArrowRight,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import FormatDate, { isMidnight } from '../utils/dateTools';
import { TruncatableHtmlContent } from '../utils/TruncatableHtmlContent';
import { API_URL, fetchData } from '../helpers/api';

const EventsPage = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [eventsPerPage] = useState(12);
    const pageTopRef = useRef(null);

    useEffect(() => {
        fetchEvents();
    }, []);

    // Scroll to top when page changes
    useEffect(() => {
        if (pageTopRef.current) {
            pageTopRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [page]);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const fetchedEvents = await fetchData(`/api/events?populate=*&filters[isPublished][$eq]=true`);
            // console.log(fetchedEvents);

            // Get current datetime as string for comparison
            const now = new Date();
            const nowString = now.toISOString().slice(0, 16).replace('T', ' '); // "2024-10-14 14:30"

            // Filter for current and future events
            const currentAndFutureEvents = fetchedEvents.filter(event => {
                // If event has an end datetime, use that for comparison
                if (event.newEndDate) {
                    return event.newEndDate >= nowString;
                }

                // For single events, check if start is in the future
                return event.newStartDate >= nowString;
            });

            // Sort events by start datetime (string comparison works perfectly!)
            const sortedEvents = currentAndFutureEvents.sort((a, b) =>
                a.newStartDate.localeCompare(b.newStartDate)
            );

            setEvents(sortedEvents);
        } catch (error) {
            console.error('Failed to fetch events:', error);
        } finally {
            setLoading(false);
        }
    };

    // Separate long-running events (spanning multiple days) from single-day events.
    // Long-running events show once with their date range; single-day events are grouped by date.
    // An event with an end date on the SAME day as the start (e.g. a conference 18h–20h) counts as single-day.
    const separateEvents = () => {
        const longRunningEvents = [];
        const dailyGroupedEvents = {};

        const today = new Date();
        const todayDateString = today.toISOString().slice(0, 10);

        events.forEach(event => {
            const startDateOnly = event.newStartDate.slice(0, 10);
            const endDateOnly = event.newEndDate ? event.newEndDate.slice(0, 10) : null;
            const isMultiDay = endDateOnly && endDateOnly > startDateOnly;

            if (isMultiDay) {
                longRunningEvents.push(event);
            } else {
                const dateKey = startDateOnly;
                if (dateKey >= todayDateString) {
                    if (!dailyGroupedEvents[dateKey]) {
                        dailyGroupedEvents[dateKey] = [];
                    }
                    dailyGroupedEvents[dateKey].push(event);
                }
            }
        });

        longRunningEvents.sort((a, b) => a.newStartDate.localeCompare(b.newStartDate));

        return { longRunningEvents, dailyGroupedEvents };
    };

    const { longRunningEvents, dailyGroupedEvents } = separateEvents();
    const sortedDateKeys = Object.keys(dailyGroupedEvents).sort((a, b) => new Date(a) - new Date(b));

    // Pagination applies to the dated agenda only; long-running events stay above and aren't paginated.
    const indexOfLastEvent = page * eventsPerPage;
    const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
    const currentDates = sortedDateKeys.slice(indexOfFirstEvent, indexOfLastEvent);
    const totalPages = Math.ceil(sortedDateKeys.length / eventsPerPage);

    // Handle page change with scrolling
    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const renderEventCard = (event) => (
        <div
            key={event.id}
            className="col"
        >
            <div className="card h-100 border-0 shadow-sm hover-translate-up transition">
                {event?.images && (
                    <div className="card-img-top position-relative">
                        <a href={`/evenement/${event?.documentId}`}>
                            <img
                                src={API_URL + event?.images[0]?.url}
                                alt={event?.title || ""}
                                className="w-100 object-fit-cover"
                                style={{ height: '200px' }}
                            />
                            {event.isFree && (
                                <span className="position-absolute top-0 end-0 badge bg-success m-2">
                                    Gratuit
                                </span>
                            )}
                        </a>
                    </div>
                )}
                <div className="card-body d-flex flex-column">
                    <div className="d-flex align-items-center text-muted small mb-2">
                        <Calendar size={14} className="me-1" />
                        {event.newEndDate ? (
                            <small>
                                <FormatDate dateString={event.newStartDate} format="short" />
                                {" - "}
                                <FormatDate dateString={event.newEndDate} format="short" />
                            </small>
                        ) : (
                            <small>
                                <FormatDate dateString={event.newStartDate} format="long" />
                                {!isMidnight(event.newStartDate) && (
                                    <>
                                        {" "}
                                        <Clock size={14} className="ms-1 me-1" />
                                        <FormatDate dateString={event.newStartDate} format="time" />
                                    </>
                                )}
                            </small>
                        )}
                    </div>

                    <h3 className="fs-6 fw-bold mb-2 line-clamp-2">
                        <a href={`/evenement/${event?.documentId}`} className='text-decoration-none text-dark'>
                            {event.name}
                        </a>
                    </h3>

                    <div className="text-muted small mb-3 flex-grow-1">
                        <TruncatableHtmlContent
                            html={event?.description}
                            truncate={true}
                            maxWords={100}
                        />
                    </div>

                    <div className="mt-auto">
                        <a
                            href={`/evenement/${event?.documentId}`}
                            className="text-dark fw-medium d-inline-flex align-items-center text-decoration-none"
                        >
                            Détails
                            <ArrowRight size={14} className="ms-1 transition-transform event-arrow" />
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderPagination = () => {
        const pageNumbers = [];

        // Always show first page
        pageNumbers.push(1);

        // Add ellipsis and surrounding pages if total pages > 5
        if (totalPages > 5) {
            // Add ellipsis and surrounding pages
            if (page > 3) {
                pageNumbers.push('...');
            }

            // Determine range of pages to show
            const startPage = Math.max(2, page - 1);
            const endPage = Math.min(totalPages - 1, page + 1);

            for (let i = startPage; i <= endPage; i++) {
                if (!pageNumbers.includes(i)) {
                    pageNumbers.push(i);
                }
            }

            // Add ellipsis if not already near the end
            if (page < totalPages - 2) {
                pageNumbers.push('...');
            }

            // Always show last page
            if (!pageNumbers.includes(totalPages)) {
                pageNumbers.push(totalPages);
            }
        } else {
            // If total pages is 5 or less, show all pages
            for (let i = 2; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        }

        return pageNumbers;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <p>Chargement des événements...</p>
            </div>
        );
    }

    return (
        <div className="events-page container py-4 mt-5">
            {/* Reference for scrolling to top */}
            <div ref={pageTopRef}></div>

            <div className="text-center mb-4">
                <h1 className="h2 fw-bold mb-2">EN CE MOMENT</h1>
                <p className="text-muted">
                    Découvrez les expositions et rendez-vous culturels du MACTe
                </p>
                <a
                    href="/evenements-passes"
                    className="btn btn-outline-secondary mt-2"
                >
                    Voir les événements passés
                </a>
            </div>

            {longRunningEvents.length === 0 && currentDates.length === 0 ? (
                <div className="text-center py-5">
                    <p className="fs-4 text-muted">Aucun événement à venir</p>
                </div>
            ) : (
                <>
                    {longRunningEvents.length > 0 && (
                        <div className="mb-5">
                            <h2 className="h5 fw-bold mb-3 pb-2 border-bottom">Rendez-vous culturels</h2>
                            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3">
                                {longRunningEvents.map(renderEventCard)}
                            </div>
                        </div>
                    )}

                    {currentDates.length > 0 && (
                        <>
                            {longRunningEvents.length > 0 && (
                                <h2 className="h5 fw-bold mb-3 pb-2 border-bottom">Agenda</h2>
                            )}
                            {currentDates.map(dateKey => {
                                const dateParts = dateKey.split('-');
                                const year = parseInt(dateParts[0]);
                                const month = parseInt(dateParts[1]) - 1;
                                const day = parseInt(dateParts[2]);

                                const headerDate = new Date(year, month, day, 12, 0, 0);

                                return (
                                    <div key={dateKey} className="mb-4">
                                        <h3 className="h6 fw-bold mb-3 pb-2 border-bottom">
                                            <FormatDate dateString={headerDate.toISOString()} format="long" />
                                        </h3>

                                        <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3">
                                            {dailyGroupedEvents[dateKey].map(renderEventCard)}
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </>
            )}

            {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <nav aria-label="Page navigation">
                        <ul className="pagination">
                            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={page === 1}
                                >
                                    <ChevronLeft size={18} />
                                </button>
                            </li>

                            {renderPagination().map((pageNum, index) => (
                                <li
                                    key={index}
                                    className={`page-item ${pageNum === page ? 'active' : ''} ${pageNum === '...' ? 'disabled' : ''}`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => typeof pageNum === 'number' && handlePageChange(pageNum)}
                                    >
                                        {pageNum}
                                    </button>
                                </li>
                            ))}

                            <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page === totalPages}
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            )}
        </div>
    );
};

export default EventsPage;